import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Message, ChatRequest } from 'types';
import { sendMessage } from 'lib/supabase-db';
import { createTempUserMessage, createErrorMessage } from 'lib/messageUtils';
import ChatContainer from 'components/chat/ChatContainer';
import { useAgentStream } from 'hooks/useAgentStream';

const Agent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // Streaming state
  const {
    isStreaming,
    streamingContent,
    error: streamError,
    startStream,
    resetStream,
  } = useAgentStream();
  const [enableStreaming] = useState(true); // Default to streaming enabled

  // Extract conversation param for dependency tracking
  const conversationParam = searchParams.get('conversation');

  // Reset loading states when conversation changes
  useEffect(() => {
    // Reset loading states when switching conversations or starting new chat
    if (conversationParam !== conversationId) {
      setIsLoading(false);
      resetStream();
    }
  }, [conversationParam, conversationId, resetStream]);

  // Handle stream errors
  useEffect(() => {
    if (streamError) {
      console.error('[Agent] Stream error detected:', streamError);

      // Stop loading state
      setIsLoading(false);

      // Add error message to chat with actual error text
      const errorMessage = createErrorMessage(conversationId, streamError);
      setMessages((prev) => [...prev, errorMessage]);

      // Reset stream (which also clears the error)
      resetStream();
    }
  }, [streamError, conversationId, resetStream]);

  // Update URL with conversation ID when a new conversation is created
  const updateUrlWithConversationId = (newConversationId: string, conversationTitle: string) => {
    if (!location.search.includes('conversation=')) {
      // Update URL using React Router navigate to trigger useSearchParams update
      navigate(`?conversation=${newConversationId}`, { replace: true });

      // Add conversation to history list by dispatching a custom event
      window.dispatchEvent(
        new CustomEvent('conversationCreated', {
          detail: {
            id: newConversationId,
            title: conversationTitle,
          },
        }),
      );
    }
  };

  const handleSendMessage = async (message: string, currentConversationId: string | null) => {
    if (!message.trim() || isLoading || isStreaming) return;

    const userMessage = message.trim();
    setInputValue('');

    // Add user message to UI immediately
    const tempUserMessage = createTempUserMessage(userMessage, currentConversationId);
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      const requestBody: ChatRequest = {
        conversation_id: currentConversationId || undefined,
        message: userMessage,
        stream: enableStreaming, // Use streaming if enabled
      };

      if (enableStreaming) {
        // ===== STREAMING MODE =====
        console.log('[Agent] Starting streaming mode');
        setIsLoading(true); // Set loading to true for thinking indicator

        // Reset previous stream content
        resetStream();

        // Start streaming
        await startStream(requestBody, (completedMessage) => {
          console.log('[Agent] Stream completed, received message:', completedMessage);

          // Update conversation ID if new
          if (!currentConversationId && completedMessage.conversation_id) {
            setConversationId(completedMessage.conversation_id);

            // Generate conversation title
            const conversationTitle = `Chat - ${new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}`;

            updateUrlWithConversationId(completedMessage.conversation_id, conversationTitle);
          }

          // Add the completed assistant message
          setMessages((prev) => [...prev, completedMessage]);

          // Reset streaming content (this will set isStreaming to false)
          resetStream();

          // Set isLoading to false only after streaming is complete
          setIsLoading(false);
        });
      } else {
        // ===== NON-STREAMING MODE (original behavior) =====
        console.log('[Agent] Using non-streaming mode');
        setIsLoading(true);

        const response = await sendMessage(requestBody);

        // Update conversation ID if new
        if (!currentConversationId) {
          setConversationId(response.conversation_id);

          // Generate conversation title
          const conversationTitle = `Chat - ${new Date().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}`;

          updateUrlWithConversationId(response.conversation_id, conversationTitle);
        }

        // Add assistant message(s)
        if (response.messages && response.messages.length > 0) {
          setMessages((prev) => [...prev, ...response.messages]);
        }

        // Set isLoading to false for non-streaming mode
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message with actual error text
      const errorText = error instanceof Error ? error.message : 'An unknown error occurred';
      const errorMessage = createErrorMessage(currentConversationId, errorText);
      setMessages((prev) => [...prev, errorMessage]);
      resetStream();
      setIsLoading(false);
    }
  };

  return (
    <ChatContainer
      onSendMessage={handleSendMessage}
      messages={messages}
      isLoading={isLoading}
      inputValue={inputValue}
      setInputValue={setInputValue}
      conversationId={conversationId}
      setConversationId={setConversationId}
      setMessages={setMessages}
      isLoadingConversation={isLoadingConversation}
      setIsLoadingConversation={setIsLoadingConversation}
      isStreaming={isStreaming}
      streamingContent={streamingContent}
    />
  );
};

export default Agent;
